<template>
  <div class="player-idle-phase">
    <IdleChromeShell :compact-layout="true" :hibernate="hibernate">
      <div class="player-idle-clock-fit">
        <div
          class="player-idle-clock-display whitespace-nowrap font-mono font-black tabular-nums tracking-tight text-white"
        >
          {{ [hours, mins, seconds].join(' : ') }}
        </div>
      </div>
    </IdleChromeShell>
  </div>
</template>

<script lang="ts" setup>
import { getPlaybackScheduleMaterializationIdentity } from '@lucimark/shared/helpers/playbackScheduleMaterializationIdentity'
import { computed, onBeforeUnmount, ref, toRefs, unref, watch } from 'vue'
import IdleChromeShell from '@/components/IdleChromeShell.vue'
import usePlayer from '@/composables/usePlayer'
import { usePlayerIdleHibernationSignals } from '@/composables/usePlayerIdleHibernationSignals'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'

const { nextCampaign, campaign } = toRefs(usePlayer().state)
const { lastEndedCampaign } = usePlayerIdleHibernationSignals()

const hours = ref('00')
const mins = ref('00')
const seconds = ref('00')
const hibernate = ref(false)

function tickClock() {
  const zoned = getLocalAsUtcInstant().toZonedDateTimeISO('UTC')
  hours.value = String(zoned.hour).padStart(2, '0')
  mins.value = String(zoned.minute).padStart(2, '0')
  seconds.value = String(zoned.second).padStart(2, '0')
}

tickClock()
const tickInterval = setInterval(() => tickClock(), 100)

const HIBERNATE_MS = 60000
let hibernateTimeout: ReturnType<typeof setTimeout> | null = null

/**
 * Stable primitive signature for idle/hibernation semantics only.
 * Avoid watching object refs: `lastEndedCampaign` is a fresh object whenever `playerGroup`
 * recomputes (e.g. registration-status polls), which would reset blackout and flash the clock.
 */
const hibernateResetSignature = computed(() => {
  const nc = unref(nextCampaign)
  const c = unref(campaign)
  const last = unref(lastEndedCampaign)
  const lastPart
    = last == null
      ? '-'
      : `${typeof last.endDate === 'string' ? last.endDate : last.endDate.toString()}\0${last.title}`
  return `${getPlaybackScheduleMaterializationIdentity(nc) ?? '∅'}|${getPlaybackScheduleMaterializationIdentity(c) ?? '∅'}|${lastPart}`
})

function scheduleHibernate() {
  if (hibernateTimeout !== null) {
    clearTimeout(hibernateTimeout)
    hibernateTimeout = null
  }
  hibernate.value = false
  hibernateTimeout = setTimeout(() => {
    hibernate.value = true
    hibernateTimeout = null
  }, HIBERNATE_MS)
}

watch(hibernateResetSignature, scheduleHibernate, { immediate: true })

onBeforeUnmount(() => {
  clearInterval(tickInterval)
  if (hibernateTimeout !== null) {
    clearTimeout(hibernateTimeout)
  }
})
</script>
